import { validate } from 'email-validator';
import { useTranslation } from 'next-i18next';
import { FC, Fragment, useEffect, useState } from 'react';
import useSWR from 'swr';

import { useActionBarContext } from '@/components/app/actionBar/ActionBarContext';
import AlertCard from '@/components/app/alertCard/AlertCard';
import { AlertCardStatus } from '@/components/app/alertCard/AlertCard.utils';
import { ExistingContract } from '@/components/listing/contract/Contract.interfaces';
import { languageOptions } from '@/components/personalParameters/utils';
import {
  CustomerInformation,
  FetchCustomer
} from '@/components/state/interfaces';
import { useEmailSuggestionLabels } from '@/hooks/useEmailSuggestionLabels/useEmailSuggestionLabels';
import { useTranslatedOptions } from '@/hooks/useTranslatedOptions/useTranslatedOptions';
import { FEATURE_NAME } from '@/hooks/useUserFeaturePermissions/interfaces';
import { useUserFeaturePermissions } from '@/hooks/useUserFeaturePermissions/useUserFeaturePermissions';
import { usePrevious } from '@/utils/helpers';
import { logError } from '@/utils/log';
import { Dictionary } from '@proprioo/hokkaido';
import { Checkbox, Loader, useDebounce, UserForm } from '@proprioo/salatim';

import ExistingCustomerEdit from '../existingCustomerWarning/ExistingCustomerEdit';
import ExistingCustomerWarning from '../existingCustomerWarning/ExistingCustomerWarning';
import ExistingCustomerWithContract from '../existingCustomerWarning/ExistingCustomerWithContract';
import { getExistingContracts } from '../existingCustomerWarning/utils';
import { CheckboxWrapper, HelperProcedure } from './CustomerForm.styles';
import { isAFakeOrRealEmail } from './utils';

export type CustomerFormProps = {
  customer: CustomerInformation;
  errors: Dictionary;
  existingCustomerButtonLabel?: string;
  hasProcedureInPending?: boolean;
  showNewsletter?: boolean;
  labels?: Dictionary;
  useActionBar?: boolean;
  onCustomerUpdate(customer: CustomerInformation): void;
  onExistingCustomerSelect?(customer: CustomerInformation): void;
  redirectToCustomer?(id: string): void;
};

const CustomerForm: FC<CustomerFormProps> = ({
  customer,
  errors,
  existingCustomerButtonLabel,
  hasProcedureInPending = false,
  labels,
  showNewsletter = true,
  useActionBar = true,
  redirectToCustomer,
  onCustomerUpdate,
  onExistingCustomerSelect
}) => {
  const { t } = useTranslation();
  const { updateSaveOptions } = useActionBarContext();

  const previousCustomer = usePrevious<CustomerInformation>(customer);
  const emailSuggestionLabels = useEmailSuggestionLabels();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [existingContracts, updateExistingContracts] = useState<
    ExistingContract[]
  >([]);
  const [email, updateEmail] = useState<string>(customer.email);
  const debounceEmail = useDebounce<string>(email, 400);

  const { data: fetchedCustomer } = useSWR<FetchCustomer>(() =>
    previousCustomer &&
    previousCustomer.email !== debounceEmail &&
    Boolean(debounceEmail) &&
    isAFakeOrRealEmail(debounceEmail) &&
    validate(debounceEmail)
      ? `/api/userByMail/${debounceEmail}`
      : null
  );

  const hasAccessToUserLanguagePreferences = useUserFeaturePermissions(
    FEATURE_NAME.CUSTOMER_LANGUAGE_PREFERENCES
  );
  const translatedLanguageOptions = useTranslatedOptions(languageOptions);

  const fetchedCustomerId = fetchedCustomer?.user.id;
  const customerId = customer.id;

  // has fetched user from email and we are on user creation form (there is no customer id on creation form)
  const fetchedUserAtCreate = Boolean(fetchedCustomerId && !customerId);

  // has fetched user from email and we are on user edit form (there is customer id).
  // We compare ids to check if fetched customer is not current customer
  const fetchedUserAtEdit = Boolean(
    customerId && fetchedCustomerId && fetchedCustomerId !== customerId
  );

  const isReplacingByExistingCustomer =
    fetchedUserAtEdit && isAFakeOrRealEmail(customer.email);

  const isExistingCustomerWithContract =
    !isLoading &&
    isReplacingByExistingCustomer &&
    Boolean(existingContracts.length) &&
    fetchedCustomerId;

  const isExistingCustomerEdit =
    !isLoading &&
    isReplacingByExistingCustomer &&
    existingContracts.length === 0;

  const onCancel = () => {
    onCustomerUpdate({ ...customer, ...previousCustomer });
    updateEmail(previousCustomer?.email || '');
  };

  useEffect(() => {
    if (useActionBar) {
      updateSaveOptions({
        disabled: !fetchedUserAtCreate || !fetchedUserAtEdit
      });
    }
  }, [fetchedUserAtEdit, fetchedUserAtCreate]);

  useEffect(() => {
    const retreveExistingContracts = async () => {
      if (!fetchedCustomerId || !customerId) {
        return;
      }

      try {
        setIsLoading(true);

        const retrievedContracts: ExistingContract[] =
          await getExistingContracts([fetchedCustomerId, customerId]);

        updateExistingContracts(retrievedContracts);
      } catch (error) {
        logError(
          `Failed to retrieve existing contracts on customer: ${customerId}`,
          { customerId, error, fetchedCustomer }
        );
      } finally {
        setIsLoading(false);
      }
    };

    retreveExistingContracts();

    return () => setIsLoading(false);
  }, [fetchedCustomerId, customerId]);

  return (
    <Fragment>
      {isLoading && <Loader text={t('loading')} />}
      {fetchedUserAtCreate && (
        <ExistingCustomerWarning
          fetchedCustomer={fetchedCustomer}
          mergeCustomerButtonLabel={existingCustomerButtonLabel}
          onCancel={onCancel}
          onSelect={() => {
            if (fetchedCustomer && onExistingCustomerSelect) {
              onExistingCustomerSelect({ ...fetchedCustomer.user });
            }
          }}
        />
      )}
      {isExistingCustomerWithContract && (
        <ExistingCustomerWithContract
          customer={customer}
          existingContracts={existingContracts}
          fetchedCustomer={fetchedCustomer}
          onCancel={onCancel}
        />
      )}
      {isExistingCustomerEdit && (
        <ExistingCustomerEdit
          customer={customer}
          fetchedCustomer={fetchedCustomer}
          onCancel={onCancel}
          redirectToCustomer={redirectToCustomer}
        />
      )}
      <UserForm
        disabledFields={{
          email: customer.isAgent || hasProcedureInPending,
          phone: hasProcedureInPending
        }}
        emailSuggestionI18nLabels={emailSuggestionLabels}
        errors={errors}
        errorMessages={{
          email: t('emailError'),
          firstName: t('firstNameError'),
          language: t('selectOptionError'),
          lastName: t('lastNameError'),
          phone: t('phoneError')
        }}
        hasLanguagePreferences={hasAccessToUserLanguagePreferences}
        labels={{
          email: t('email'),
          firstName: t('firstNameRequired'),
          language: t('languagePreferences'),
          lastName: t('lastNameRequired'),
          phone: t('phoneRequired'),
          ...labels
        }}
        phoneWithFlags={true}
        updateUser={newCustomer => {
          onCustomerUpdate({ ...customer, ...newCustomer });
          updateEmail(newCustomer.email);
        }}
        user={customer}
        userLanguageOptions={translatedLanguageOptions}
      />
      {hasProcedureInPending && (
        <HelperProcedure>
          <AlertCard status={AlertCardStatus.INFO}>
            {t('emailPhoneInProcedure')}
          </AlertCard>
        </HelperProcedure>
      )}

      {showNewsletter && (
        <CheckboxWrapper>
          <Checkbox
            checked={customer.promotionalConsent}
            label={t('registerCustomerToNewsletter')}
            disabled={false}
            name={'checkbox-newsletter'}
            dataTest="checkbox-newsletter"
            onChange={checked =>
              onCustomerUpdate({
                ...customer,
                promotionalConsent: checked
              })
            }
            id="newsletter"
          />
        </CheckboxWrapper>
      )}
    </Fragment>
  );
};

export default CustomerForm;
